import { useState } from "react";
import { toast } from "sonner";
import { Check, ZoomIn } from "lucide-react";
import { HydrangeaFlourish, DahliaFlourish, RoseFlourish } from "@/components/wedding/Flourish";
import { Lightbox } from "@/components/wedding/Lightbox";
import { PALETTE } from "@/data/palette";
import { useLang } from "@/i18n/LanguageContext";

const INSPO_PHOTOS = [
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/hii0j2i4_inspo1.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/zcbqfvtv_inspo%202.jpg",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/y8v71n74_WhatsApp%20Image%202026-07-31%20at%2021.02.06.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/jydcg09s_WhatsApp%20Image%202026-07-31%20at%2021.02.06%20%283%29.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/gf6d6bvk_WhatsApp%20Image%202026-07-31%20at%2021.02.06%20%282%29.jpeg",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/gj2hhx1l_WhatsApp%20Image%202026-07-31%20at%2021.02.06%20%281%29.jpeg",
];

const Swatch = ({ hex, prohibited = false, warningLabel, size = "md" }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopied(true);
      toast.success(`${hex} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const dim =
    size === "lg" ? "w-24 h-24 sm:w-32 sm:h-32" :
    size === "sm" ? "w-14 h-14 sm:w-16 sm:h-16" :
    "w-20 h-20 sm:w-24 sm:h-24";

  return (
    <div className="flex flex-col items-center text-center gap-3 group">
      <button
        onClick={copy}
        aria-label={`Copy ${hex}`}
        className={`relative ${dim} rounded-full transition-transform duration-300 group-hover:scale-105 focus:outline-none focus:ring-2 focus:ring-black/40 focus:ring-offset-2`}
        style={{
          backgroundColor: hex,
          boxShadow:
            "0 12px 30px -12px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(0,0,0,0.06)",
        }}
      >
        {prohibited && (
          <span
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, transparent calc(50% - 2px), #FF0054 calc(50% - 1px), #FF0054 calc(50% + 1px), transparent calc(50% + 2px))",
            }}
            aria-hidden
          />
        )}
        {copied && (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/70 text-white">
            <Check size={size === "sm" ? 14 : 20} />
          </span>
        )}
      </button>
      {prohibited && warningLabel && (
        <span className="mt-1 font-serif italic text-xs sm:text-sm text-[color:var(--sunset-600)] leading-snug max-w-[9rem]">
          {warningLabel}
        </span>
      )}
    </div>
  );
};

const Group = ({ title, colors, warningLabel, size = "md", subtitle }) => (
  <div className="mt-16 first:mt-0">
    <div className="flex items-baseline gap-4 mb-10 flex-wrap">
      <span className="w-8 h-px bg-black/30 self-center" />
      <h3 className="font-serif italic text-2xl sm:text-3xl text-black/80">{title}</h3>
      {subtitle && (
        <span className="font-overline text-[10px] text-black/50">{subtitle}</span>
      )}
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-10 justify-items-center">
      {colors.map((c) => (
        <Swatch
          key={c.hex}
          hex={c.hex}
          prohibited={!!c.prohibited}
          warningLabel={warningLabel}
          size={size}
        />
      ))}
    </div>
  </div>
);

export const Inspiration = () => {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(-1);

  return (
    <section id="inspiration" data-testid="inspiration-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] sunset-orb opacity-40 pointer-events-none" aria-hidden />
      <HydrangeaFlourish className="absolute -top-6 -left-10 w-[100px] sm:w-[140px] md:w-[180px]" opacity={0.7} />
      <DahliaFlourish className="absolute -top-4 -right-10 w-[100px] sm:w-[140px] md:w-[180px]" opacity={0.7} />
      <RoseFlourish
        className="absolute -bottom-6 -left-6 w-[100px] sm:w-[140px] md:w-[180px] -rotate-3"
        opacity={0.75}
      />

      {/* Header block */}
      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 text-center">
        <div className="font-overline text-[11px] tracking-[0.4em] text-black/60">
          {t("inspiration.eyebrow")}
        </div>
        <h2 className="mt-6 font-display text-6xl sm:text-8xl lg:text-9xl tracking-tighter font-light">
          {t("inspiration.title")}
        </h2>
        <div className="mt-4 flex justify-center">
          <span className="block w-28 h-px bg-black/30" />
        </div>
        <p className="mt-10 font-serif italic text-lg sm:text-xl text-black/70 max-w-2xl mx-auto leading-relaxed">
          {t("inspiration.intro")}
        </p>
      </div>

      {/* Palette */}
      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 mt-24 sm:mt-28">
        <Group
          title={t("inspiration.primary")}
          colors={PALETTE.primary}
          warningLabel={t("inspiration.whiteWarning")}
          size="lg"
        />
        <Group
          title={t("inspiration.secondary")}
          colors={PALETTE.secondary}
          subtitle={t("inspiration.optional")}
        />
        <Group
          title={t("inspiration.accent")}
          colors={PALETTE.accent}
          subtitle={t("inspiration.optional")}
          size="sm"
        />
      </div>

      {/* Mood board */}
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 mt-32 sm:mt-40">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="font-overline text-[11px] text-[color:var(--sunset-600)]">
              {t("inspiration.photosEyebrow")}
            </div>
            <h3 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light">
              {t("inspiration.photosTitle")}
            </h3>
          </div>
          <p className="font-serif italic text-lg text-black/60 max-w-sm">
            {t("inspiration.photosSub")}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {INSPO_PHOTOS.map((src, i) => (
            <figure key={i} className="relative aspect-[4/5] overflow-hidden bg-[color:var(--sunset-50)] group">
              <button
                type="button"
                data-testid={`inspo-photo-${i}`}
                aria-label={`Open inspiration photo ${i + 1}`}
                onClick={() => setOpenIdx(i)}
                className="absolute inset-0 flex items-center justify-center cursor-zoom-in"
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <span className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-black/60 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn size={14} />
                </span>
              </button>
            </figure>
          ))}
        </div>
      </div>

      <Lightbox
        images={INSPO_PHOTOS}
        openIdx={openIdx}
        onClose={() => setOpenIdx(-1)}
        onNav={setOpenIdx}
        objectFit="contain"
        testId="inspo-lightbox"
      />
    </section>
  );
};
